/**
 * useRecordingList
 *
 * Queries the connected device for its event-indexed recording list via the
 * SDK's runtime.listRecordings() method. All signaling-server WebSocket
 * plumbing now lives inside `@vgps/livekit_web` (as of 0.1.7).
 *
 * Each event record contains camelCase fields normalized by the SDK:
 * eventId, tsUs, cameraMask, slotId, type, score, preRollMs, postRollMs, durationMs.
 */
import { useCallback, useState } from 'react';
import { useVgpsRuntime } from '@vgps/livekit_web/react';

/**
 * @param {string} deviceSerial  - e.g. "862635067769554"
 * @returns {{ recordings, loading, error, refresh }}
 *   refresh: (opts?: { sinceMs?, untilMs?, maxCount?, order?, timeoutMs? }) => Promise<void>
 */
export function useRecordingList(deviceSerial) {
  const runtime = useVgpsRuntime();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const refresh = useCallback(async (opts) => {
    if (!runtime || !deviceSerial) return;
    setLoading(true);
    setError(null);
    try {
      const events = await runtime.listRecordings({ deviceSerial, ...opts });
      setRecordings(events);
    } catch (err) {
      setError(err.message || String(err));
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  }, [runtime, deviceSerial]);

  return { recordings, loading, error, refresh };
}
